import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import type { ReadAiAssignment, ReadAiOpenPr, ReadAiRetarget, ReadAiRetargetTarget } from "../meetingIntelligence.types";
import { currentUserIdOf } from "../../../auth/userId";
import { isOperator } from "../../../auth/tier";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * [pr-feedback] The item's open PR as one line: the PR, its merge status, its
 * last build's result, and — for a failed build — why, as HQ read it from
 * Azure DevOps. Says only what HQ heard: no build yet is "no build reported",
 * never "not built"; a reason still being read says so rather than showing
 * nothing.
 */
export const openPrLine = (
  pr: ReadAiOpenPr | null | undefined,
): { label: string; url: string | null; status: string; failure: string | null; buildUrl: string | null } | null => {
  if (!pr) return null;
  const parts = [pr.mergeStatus ? `merge: ${pr.mergeStatus}` : "merge status not reported"];
  const b = pr.build;
  parts.push(b ? `build ${b.id}: ${b.result ?? "no result"}` : "no build reported");
  let failure: string | null = null;
  if (b?.failureState === "pending") failure = "Reading why the build failed…";
  else if (b?.failureState === "read" || b?.failureState === "unreadable") failure = b.failure;
  return {
    label: `PR #${pr.id}${pr.repository ? ` in ${pr.repository}` : ""}`,
    url: httpUrlOrNull(pr.url),
    status: parts.join(" · "),
    failure,
    buildUrl: httpUrlOrNull(b?.url),
  };
};

/** [retarget] A target in words: what a builder places the work by, in its order (slug, package, client). */
export const retargetTargetWords = (t: ReadAiRetargetTarget | null | undefined): string => {
  if (!t) return "no target";
  if (t.governedSlug) return `module ${t.governedSlug}`;
  if (t.packageName) return `package ${t.packageName}`;
  if (t.clientCode) return `client ${t.clientCode}`;
  return "no target";
};

const dateOf = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

/**
 * [retarget] The newest retarget as ONE line: "Routed to module <slug> by
 * <by> on <date>; was <previous>; undo until <date>". An undone one says when
 * it was undone instead of the window; one whose window closed says that. The
 * line says what HQ recorded — it does not claim the Azure DevOps card moved,
 * because HQ does not move it.
 */
export const retargetLine = (r: ReadAiRetarget | null | undefined, now: Date = new Date()): string | null => {
  if (!r) return null;
  const to = `${retargetTargetWords(r.current)}${r.work ? ` (${r.work})` : ""}`;
  const head = `Routed to ${to} by ${r.by || "someone unrecorded"}${dateOf(r.at) ? ` on ${dateOf(r.at)}` : ""}; was ${retargetTargetWords(r.previous)}`;
  if (r.undone) return `${head}; undone on ${dateOf(r.undone) ?? r.undone}`;
  const until = r.undoUntil ? new Date(r.undoUntil) : null;
  if (!until || Number.isNaN(until.getTime())) return `${head}; it cannot be undone`;
  return until.getTime() > now.getTime() ? `${head}; undo until ${dateOf(r.undoUntil)}` : `${head}; the undo window closed ${dateOf(r.undoUntil)}`;
};

/**
 * [retarget] Whether THIS person is offered Undo, mirroring the API
 * (`undoRetarget`): their own accepted item, a newest retarget not yet undone,
 * inside its window. A hint for the button; the API decides.
 */
export const undoRetargetOffer = (
  a: Pick<ReadAiAssignment, "status" | "userId" | "retarget" | "actionItemId"> | null,
  me: number | null,
  now: Date = new Date(),
): boolean => {
  const r = a?.retarget;
  if (!a || !r || r.undone || me === null || a.actionItemId == null) return false;
  if (a.status !== "accepted" && a.status !== "assigned") return false;
  if (a.userId == null || Number(a.userId) !== me) return false;
  const until = r.undoUntil ? new Date(r.undoUntil).getTime() : NaN;
  return Number.isFinite(until) && until > now.getTime();
};

/** The slice of the RAW Feathers service Accept calls (it rejects on every refusal). */
interface RawAssignments {
  patch: (id: number, data: object) => Promise<unknown>;
}

/**
 * Whether a question holds this item: one this person may see names it
 * (`heldByInquiryIds`), or — operator ruling B (2026-11-02) — the item is held
 * (`pending` on an `available` assignment) by one they may not see, which the
 * API does not name to them. Either way it is not theirs to accept yet.
 */
export const isHeld = (a: Pick<ReadAiAssignment, "status" | "heldByInquiryIds" | "itemDistributionStatus"> | null): boolean =>
  !!a && ((a.heldByInquiryIds ?? []).length > 0 || (a.status === "available" && a.itemDistributionStatus === "pending"));

/**
 * [hub] review round 1 — whether THIS person is offered Accept on this item,
 * mirroring the API (read-ai-assignments `acceptAuthority`): an `available`
 * item that no open question holds, and either their own or — with operator
 * or admin access — someone else's, which HQ records as accepted on the
 * owner's behalf. A hint for the button; the API decides.
 */
export const acceptOffer = (
  a: Pick<ReadAiAssignment, "status" | "userId" | "heldByInquiryIds" | "itemDistributionStatus"> | null,
  me: number | null,
  operator: boolean,
): "own" | "on-behalf" | null => {
  if (!a || a.status !== "available" || me === null) return null;
  if (isHeld(a)) return null;
  if (a.userId != null && Number(a.userId) === me) return "own";
  return operator ? "on-behalf" : null;
};

/**
 * A link a person may be sent to, or null. Only http(s) — the PR URL is written
 * by whoever completed the item (a developer's Gantry, an operator), and an
 * `href` is executable when it says `javascript:`.
 */
export const httpUrlOrNull = (raw: string | null | undefined): string | null => {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  try {
    const url = new URL(raw.trim());
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
};

/** What a failed load means to the person looking at it. */
export const loadFailureMessage = (err: unknown): string => {
  const code = (err as { code?: unknown } | null)?.code;
  if (code === 404) return "This action item no longer exists.";
  if (code === 401 || code === 403) return "You do not have access to this action item.";
  return err instanceof Error && err.message ? err.message : "Failed to load the action item.";
};

/**
 * Route ViewStore for the Action Item screen — `/app/assignments/:id`, the page
 * every action-item notification links to (API `read-ai/assignment-link.ts`).
 *
 * A plain `makeAutoObservable` class (web CLAUDE.md). It holds the id and the
 * load state only: the assignment itself is fetched through the registered
 * `read-ai-assignments` ServiceStore, whose `get` lands it in `entities`, and is
 * read back from there with `.entities.find` — never kept here.
 */
class AssignmentDetailStore {
  rootStore: RootStore;

  assignmentId: number | null = null;
  loading = false;
  error: string | null = null;
  /** [hub] review round 1: an Accept in flight, and what it said. */
  accepting = false;
  acceptError: string | null = null;
  acceptNotice: string | null = null;
  /** [retarget] An Undo in flight, and what it said. */
  undoing = false;
  undoError: string | null = null;
  undoNotice: string | null = null;
  /** Which load is current — a counter, not data. */
  private loadRequest = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `read-ai-assignments` ServiceStore. */
  private get assignmentsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("read-ai-assignments") ??
        this.rootStore.readAiAssignmentsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The assignment, from the ServiceStore's cache. */
  get assignment(): ReadAiAssignment | null {
    if (this.assignmentId === null) return null;
    const entities = (this.assignmentsService?.entities ?? []) as unknown as ReadAiAssignment[];
    return entities.find((a) => Number(a.id) === this.assignmentId) ?? null;
  }

  /** The Azure DevOps work item, when one was filed and its URL is a web link. */
  get workItemUrl(): string | null {
    return httpUrlOrNull(this.assignment?.externalWorkItemUrl);
  }

  /** The PR the completion recorded, when there is one and it is a web link. */
  get prUrl(): string | null {
    return httpUrlOrNull(this.assignment?.completedPrUrl);
  }

  /** [pr-feedback] The open PR, as one line (see `openPrLine`). */
  get openPr() {
    return openPrLine(this.assignment?.openPr);
  }

  /** Which Accept, if any, this person is offered. Read on every render (the auth store is not this store's state). */
  acceptOfferFor(): "own" | "on-behalf" | null {
    return acceptOffer(this.assignment, currentUserIdOf(this.rootStore), isOperator(this.rootStore));
  }

  private get raw(): RawAssignments | null {
    return this.rawService("read-ai-assignments");
  }

  private rawService(path: string): RawAssignments | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawAssignments } }).app;
    return app?.service(path) ?? null;
  }

  /** [retarget] The newest retarget as one line (see `retargetLine`). */
  get retargetSummary(): string | null {
    return retargetLine(this.assignment?.retarget);
  }

  /** [retarget] Whether this person is offered Undo. Read on every render, like `acceptOfferFor`. */
  undoRetargetOfferFor(): boolean {
    return undoRetargetOffer(this.assignment, currentUserIdOf(this.rootStore));
  }

  /**
   * [retarget] Put back the target the newest retarget replaced. Goes through
   * `developer-action-items` (keyed by the ACTION ITEM id), the same verb the
   * owner's Gantry calls, so there is one undo and HQ records it the same way.
   */
  async undoRetarget(): Promise<boolean> {
    const itemId = this.assignment?.actionItemId ?? null;
    const raw = this.rawService("developer-action-items");
    if (itemId == null || !raw || this.undoing) return false;
    this.undoing = true;
    this.undoError = null;
    this.undoNotice = null;
    try {
      const result = (await raw.patch(itemId, { action: "retarget-undo" })) as { restored?: ReadAiRetargetTarget } | null;
      runInAction(() => {
        this.undoNotice = `Undone. The item is back on ${retargetTargetWords(result?.restored)}.`;
      });
      await this.load();
      return true;
    } catch (err: unknown) {
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.undoError = err instanceof Error && err.message ? err.message : "The undo failed.";
      });
      await this.load();
      return false;
    } finally {
      runInAction(() => {
        this.undoing = false;
      });
    }
  }

  /**
   * Accept the item: "I am doing this". This is what creates the Azure DevOps
   * work item, and — since ruling D7 of 2026-10-19 — the only place building
   * starts: the owner's Gantry builds what was accepted here.
   */
  async accept(): Promise<boolean> {
    const id = this.assignmentId;
    const raw = this.raw;
    if (id === null || !raw || this.accepting) return false;
    this.accepting = true;
    this.acceptError = null;
    this.acceptNotice = null;
    try {
      const result = (await raw.patch(id, { action: "accept" })) as { workItem?: { state?: string; detail?: string | null } } | null;
      runInAction(() => {
        this.acceptNotice =
          result?.workItem?.state === "deferred"
            ? `Accepted. The Azure DevOps work item could not be filed yet (${result.workItem.detail ?? "HQ will keep retrying"}).`
            : "Accepted. Your Gantry builds it from here.";
      });
      await this.load();
      return true;
    } catch (err: unknown) {
      // A sign-out: no error, and no re-read — reset() cleared assignmentId,
      // so load() would write "This link does not name an action item." onto
      // the next person's page.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.acceptError = err instanceof Error && err.message ? err.message : "The accept failed.";
      });
      await this.load();
      return false;
    } finally {
      runInAction(() => {
        this.accepting = false;
      });
    }
  }

  /** A route param in, a positive integer id or null. */
  setAssignmentId(raw: string | number | null | undefined) {
    const n = Number(raw);
    this.assignmentId = Number.isInteger(n) && n > 0 ? n : null;
  }

  async load() {
    const id = this.assignmentId;
    if (id === null) {
      this.error = "This link does not name an action item.";
      return;
    }
    const service = this.assignmentsService;
    if (!service) {
      this.error = "Action items are not available right now.";
      return;
    }
    // Only the LATEST load may settle the page's state: navigating 42 → 43
    // while 42 is in flight must not let 42's answer clear 43's spinner
    // (Codex review).
    const request = ++this.loadRequest;
    this.loading = true;
    this.error = null;
    try {
      await service.get(id, { throwOnError: true } as never);
    } catch (err: unknown) {
      runInAction(() => {
        if (request === this.loadRequest) { if (!isSessionEnded(err)) this.error = loadFailureMessage(err); }
      });
    } finally {
      runInAction(() => {
        if (request === this.loadRequest) this.loading = false;
      });
    }
  }

  reset() {
    this.loadRequest++;
    this.assignmentId = null;
    this.loading = false;
    this.error = null;
    this.accepting = false;
    this.acceptError = null;
    this.acceptNotice = null;
    this.undoing = false;
    this.undoError = null;
    this.undoNotice = null;
  }
}

export default AssignmentDetailStore;
